/**
 * SparkLayer <spark-form> paints into an open shadow root and resets
 * --spark-form-* on :host (including max-width: 680px). Theme CSS cannot
 * reach those nodes, so we inject rules against SparkLayer’s real classes:
 *
 *   .spark-form-wrapper.py-6.sm:py-8
 *   section.space-y-4
 *   .space-y-2.5 > label.text-h6 + input|textarea|select
 *   fieldset .grid.grid-cols-1.md:grid-cols-2
 *   footer.mt-8.border-t > nav[aria-label="Form navigation"] > button.sl-button-width
 */
(function () {
  if (window.__goSparkContactForm) return;
  window.__goSparkContactForm = true;

  const STYLE_ATTR = 'data-go-spark-contact';

  const injectedCss = `
    :host {
      --spark-form-max-width: none;
      --spark-form-font-default-size: 16px;
      --spark-form-font-default-size-small: 16px;
      --spark-form-h6-fontsize: 14px;
      --spark-form-input-background: #f9f9f9;
      --spark-form-input-border-color: rgba(0, 0, 0, 0.2);
      --spark-form-input-focus-border-color: #212b3c;
      --spark-form-input-text-color: #212121;
      --spark-form-input-padding: 10px 18px;
      --spark-form-border-radius-default: 25px;
      --spark-form-border-radius-button: 50px;
      --spark-form-button-width: 250px;
      --spark-form-button-padding: 13px 17px;
      --spark-form-button-font-size: 14px;
      --spark-form-button-font-weight: 600;
      --spark-form-button-text-transform: uppercase;
      --spark-form-button-raised-background: #212b3c;
      --spark-form-button-raised-hover-background: #283346;
      --spark-form-button-raised-color: #ffffff;
      --spark-form-button-raised-border-color: rgba(0, 0, 0, 0.1);
      --spark-form-primary-color: #0a0a0a;
      --spark-form-header-color: #0a0a0a;
      --spark-form-default-body-color: #3a3a40;
      --spark-placeholder-text-color: #505050;
      --spark-input-background: #f9f9f9;
      --spark-input-border-color: rgba(0, 0, 0, 0.2);
      --spark-input-padding: 10px 18px;
      --spark-input-radius: 25px;
      --spark-border-radius-default: 25px;
      --spark-border-radius-button: 50px;
      font-family: inherit;
    }

    .spark-form-wrapper {
      width: 100% !important;
      max-width: none !important;
      padding: 0 !important;
    }

    .max-w-\\[680px\\],
    .mx-auto {
      max-width: none !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .px-4,
    .sm\\:px-6,
    .py-6,
    .sm\\:py-8 {
      padding: 0 !important;
    }

    .spark-form-wrapper > .mb-6.space-y-2 {
      display: none !important;
    }

    .spark-form-wrapper > section.space-y-4 {
      display: flex !important;
      flex-direction: column !important;
      gap: 32px !important;
    }

    .spark-form-wrapper > section.space-y-4 > :not([hidden]) ~ :not([hidden]) {
      margin-top: 0 !important;
    }

    .space-y-2\\.5 {
      display: flex !important;
      flex-direction: column !important;
      gap: 13px !important;
    }

    .space-y-2\\.5 > :not([hidden]) ~ :not([hidden]) {
      margin-top: 0 !important;
    }

    .grid.grid-cols-1 {
      gap: 32px !important;
    }

    @media (min-width: 768px) {
      .md\\:grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 32px !important;
      }
    }

    fieldset > legend {
      display: none !important;
    }

    label.text-h6,
    label.text-h6.text-error,
    legend.text-h6,
    legend.text-error {
      font-size: 14px !important;
      font-weight: 600 !important;
      line-height: 18px !important;
      color: #0a0a0a !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
    }

    label .text-error,
    legend .text-error {
      color: inherit !important;
    }

    input.border-input,
    select.border-input,
    textarea.border-input {
      width: 100% !important;
      min-height: 44px !important;
      padding: 10px 18px !important;
      background-color: #f9f9f9 !important;
      border: 1px solid rgba(0, 0, 0, 0.2) !important;
      border-radius: 25px !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      line-height: 26px !important;
      color: #212121 !important;
      box-shadow: none !important;
    }

    select.border-input {
      padding-right: 44px !important;
      background-image: none !important;
    }

    .relative.inline-flex.w-full > span.pointer-events-none.absolute {
      top: 50% !important;
      right: 18px !important;
      left: auto !important;
      inset-inline-start: auto !important;
      inset-inline-end: 18px !important;
      width: 12px !important;
      height: 12px !important;
      transform: translateY(-50%) !important;
      color: transparent !important;
      background-image: var(--spark-contact-select-arrow) !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 8px 4px !important;
      z-index: 2 !important;
    }

    .relative.inline-flex.w-full > span.pointer-events-none.absolute svg {
      display: none !important;
    }

    textarea.border-input,
    textarea.min-h-\\[12rem\\] {
      min-height: 152px !important;
      border-radius: 24px !important;
      line-height: 26px !important;
    }

    footer.mt-8.border-t,
    footer.border-input-border {
      margin-top: 24px !important;
      padding-top: 0 !important;
      border: 0 !important;
    }

    nav[aria-label="Form navigation"] {
      justify-content: flex-start !important;
    }

    nav[aria-label="Form navigation"] .sm\\:ml-auto {
      margin-left: 0 !important;
    }

    nav[aria-label="Form navigation"] button.sl-button-width,
    button.bg-btn-raised-bg {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      width: 250px !important;
      max-width: 100% !important;
      height: 48px !important;
      padding: 13px 17px !important;
      background-color: #212b3c !important;
      border: 1px solid rgba(0, 0, 0, 0.1) !important;
      border-radius: 50px !important;
      color: #fff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      line-height: 18px !important;
      letter-spacing: 0 !important;
      text-transform: uppercase !important;
    }

    nav[aria-label="Form navigation"] button.sl-button-width::after,
    button.bg-btn-raised-bg::after {
      content: "" !important;
      display: block !important;
      flex: 0 0 24px !important;
      width: 24px !important;
      height: 24px !important;
      background-image: var(--spark-contact-submit-arrow) !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background-size: 24px 24px !important;
      background-color: transparent !important;
    }
  `;

  function inject(host) {
    const root = host.shadowRoot;
    if (!root) return false;
    let style = root.querySelector('style[' + STYLE_ATTR + ']');
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(STYLE_ATTR, '');
      root.appendChild(style);
    }
    style.textContent = injectedCss;
    return true;
  }

  function observe(host) {
    if (!host || host.__goSparkMo || !host.closest('.spark-contact-form')) return;

    inject(host);

    if (!host.shadowRoot || typeof MutationObserver === 'undefined') return;
    host.__goSparkMo = true;
    new MutationObserver(function () {
      inject(host);
    }).observe(host.shadowRoot, { childList: true });
  }

  function scan() {
    document.querySelectorAll('.spark-contact-form spark-form').forEach(observe);
  }

  function setFormOpen(open) {
    const form = document.getElementById('getintouch');
    if (!form) return;

    form.classList.toggle('is-open', open);
    if (open) {
      form.removeAttribute('aria-hidden');
    } else if (form.classList.contains('is-hidden-until-open')) {
      form.setAttribute('aria-hidden', 'true');
    }

    document.querySelectorAll('[data-contact-form-toggle]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', String(open));
      trigger.classList.toggle('is-open', open);
    });

    if (open) {
      requestAnimationFrame(function () {
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function initToggle() {
    document.addEventListener('click', function (event) {
      const toggle = event.target.closest('[data-contact-form-toggle]');
      if (toggle) {
        event.preventDefault();
        const form = document.getElementById('getintouch');
        setFormOpen(!(form && form.classList.contains('is-open')));
        return;
      }

      const hashLink = event.target.closest('a[href="#getintouch"]');
      if (hashLink) {
        event.preventDefault();
        setFormOpen(true);
      }
    });

    if (window.location.hash === '#getintouch') {
      setFormOpen(true);
    }
  }

  function init() {
    scan();
    initToggle();
    if (typeof MutationObserver === 'undefined') return;

    let scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        scan();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
